/**
 * WorkstreamBreakdownPanel - Expandable accordion of workstream details.
 *
 * Shows each workstream with its key questions, findings, synthesis,
 * and nested agent details with full output.
 */

import { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, Cpu, Copy, CheckCircle2, Expand, Minimize2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { StrategicWorkstream, StrategicAgent } from '@/hooks/useStrategicStream';

// Matches WORKSTREAM_COLORS from StrategicLogicTree.tsx:42-48
const WORKSTREAM_COLORS = [
  { border: 'border-green-500/30', bg: 'bg-green-500/10', text: 'text-green-400', dot: 'bg-green-400' },
  { border: 'border-orange-500/30', bg: 'bg-orange-500/10', text: 'text-orange-400', dot: 'bg-orange-400' },
  { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-400' },
  { border: 'border-purple-500/30', bg: 'bg-purple-500/10', text: 'text-purple-400', dot: 'bg-purple-400' },
  { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', text: 'text-yellow-400', dot: 'bg-yellow-400' },
];

interface WorkstreamBreakdownPanelProps {
  workstreams: StrategicWorkstream[];
  className?: string;
}

export function WorkstreamBreakdownPanel({ workstreams, className }: WorkstreamBreakdownPanelProps) {
  const [expandedWorkstreams, setExpandedWorkstreams] = useState<Record<string, boolean>>({});
  const [expandedAgents, setExpandedAgents] = useState<Record<string, boolean>>({});
  const [copiedAgent, setCopiedAgent] = useState<string | null>(null);

  const toggleWorkstream = useCallback((id: string) => {
    setExpandedWorkstreams(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleAgent = useCallback((key: string) => {
    setExpandedAgents(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const copyOutput = useCallback(async (text: string, agentKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAgent(agentKey);
      setTimeout(() => setCopiedAgent(null), 2000);
    } catch { /* ignore clipboard errors */ }
  }, []);

  const allExpanded = useMemo(() => {
    return workstreams.length > 0 && workstreams.every(ws => expandedWorkstreams[ws.id]);
  }, [workstreams, expandedWorkstreams]);

  const toggleAll = useCallback(() => {
    const newState: Record<string, boolean> = {};
    workstreams.forEach(ws => { newState[ws.id] = !allExpanded; });
    setExpandedWorkstreams(newState);
  }, [workstreams, allExpanded]);

  if (workstreams.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted text-sm">
        No workstream data available for this analysis.
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Expand All / Collapse All toggle */}
      <div className="flex justify-end">
        <button
          onClick={toggleAll}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-light/50 transition-colors"
        >
          {allExpanded ? (
            <>
              <Minimize2 className="w-3.5 h-3.5" />
              Collapse All
            </>
          ) : (
            <>
              <Expand className="w-3.5 h-3.5" />
              Expand All
            </>
          )}
        </button>
      </div>

      {workstreams.map((ws, wsIndex) => {
        const color = WORKSTREAM_COLORS[wsIndex % WORKSTREAM_COLORS.length];
        const isExpanded = expandedWorkstreams[ws.id] ?? false;

        return (
          <div key={ws.id} className={cn('rounded-lg border', color.border, 'bg-surface/50')}>
            {/* Workstream header */}
            <button
              onClick={() => toggleWorkstream(ws.id)}
              aria-expanded={isExpanded}
              aria-controls={`workstream-content-${ws.id}`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-light/30 transition-colors rounded-lg"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', color.dot)} />
                <span className={cn('text-sm font-medium truncate', color.text)}>
                  {ws.name}
                </span>
                <span className="text-xs text-text-muted flex-shrink-0">
                  {ws.agents.length} agents
                </span>
                {ws.totalCitations > 0 && (
                  <span className="text-xs text-text-muted flex-shrink-0">
                    {ws.totalCitations} citations
                  </span>
                )}
                {ws.latencySeconds != null && (
                  <span className="text-xs text-text-muted flex-shrink-0">
                    {ws.latencySeconds.toFixed(1)}s
                  </span>
                )}
              </div>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-text-muted flex-shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 text-text-muted flex-shrink-0" />
              )}
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div id={`workstream-content-${ws.id}`} role="region" className="px-4 pb-4 space-y-4">
                {/* Key questions */}
                {ws.keyQuestions.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5">
                      Key Questions
                    </div>
                    <ol className="space-y-1 list-decimal list-inside">
                      {ws.keyQuestions.map((q, i) => (
                        <li key={i} className="text-[15px] text-text-secondary">{q}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Key findings */}
                {ws.keyFindings.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5">
                      Key Findings
                    </div>
                    <ul className="space-y-1.5">
                      {ws.keyFindings.map((finding, i) => (
                        <li key={i} className="flex items-start gap-2 text-[15px] text-text-primary">
                          <CheckCircle2 className={cn('w-3.5 h-3.5 mt-0.5 flex-shrink-0', color.text)} />
                          <MarkdownContent content={finding} className="flex-1 [&_p]:inline text-[15px]" inline />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Synthesis preview */}
                {ws.synthesisPreview && (
                  <div>
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5">
                      Synthesis
                    </div>
                    <div className="text-[15px] text-text-primary leading-relaxed rounded-lg bg-surface-light/50 border border-border/30 p-3">
                      <MarkdownContent content={ws.synthesisPreview} className="text-[15px]" />
                    </div>
                  </div>
                )}

                {/* Agent details */}
                {ws.agents.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5">
                      Agent Details
                    </div>
                    <div className="space-y-2">
                      {ws.agents.map((agent) => (
                        <AgentCard
                          key={`${ws.id}-${agent.agentIndex}`}
                          agent={agent}
                          isExpanded={expandedAgents[`${ws.id}-${agent.agentIndex}`] ?? false}
                          onToggle={() => toggleAgent(`${ws.id}-${agent.agentIndex}`)}
                          onCopy={(text) => copyOutput(text, `${ws.id}-${agent.agentIndex}`)}
                          isCopied={copiedAgent === `${ws.id}-${agent.agentIndex}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AgentCard({
  agent,
  isExpanded,
  onToggle,
  onCopy,
  isCopied,
}: {
  agent: StrategicAgent;
  isExpanded: boolean;
  onToggle: () => void;
  onCopy: (text: string) => void;
  isCopied: boolean;
}) {
  const hasOutput = !!agent.fullOutput || !!agent.outputPreview;

  return (
    <div className="rounded-lg bg-surface-light/40 border border-border/30">
      <button
        onClick={onToggle}
        disabled={!hasOutput}
        aria-expanded={isExpanded}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 text-left rounded-lg transition-colors',
          hasOutput ? 'hover:bg-surface-light/60 cursor-pointer' : 'cursor-default'
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Cpu className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
          <span className="text-xs font-medium text-text-primary truncate">
            {agent.agentRole || `Agent ${agent.agentIndex + 1}`}
          </span>
          {agent.model && (
            <span className="text-[10px] text-text-muted flex-shrink-0">
              ({agent.model})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {agent.citationCount > 0 && (
            <span className="text-[10px] text-text-muted">{agent.citationCount} cit.</span>
          )}
          {agent.latencySeconds != null && (
            <span className="text-[10px] text-text-muted">{agent.latencySeconds.toFixed(1)}s</span>
          )}
          {hasOutput && (
            isExpanded ? (
              <ChevronUp className="w-3 h-3 text-text-muted" />
            ) : (
              <ChevronDown className="w-3 h-3 text-text-muted" />
            )
          )}
        </div>
      </button>

      {isExpanded && hasOutput && (
        <div className="px-3 pb-3">
          {/* Key findings */}
          {agent.keyFindings && agent.keyFindings.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] font-medium text-text-muted uppercase mb-1">Findings</div>
              <ul className="space-y-0.5">
                {agent.keyFindings.map((f, i) => (
                  <li key={i} className="text-[13px] text-text-secondary flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-text-muted mt-1.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Full output */}
          <div className="relative">
            <div className="max-h-[200px] overflow-y-auto rounded-lg bg-surface/70 border border-border/20 p-2 text-[13px] text-text-secondary leading-relaxed scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
              <MarkdownContent content={agent.fullOutput || agent.outputPreview || ''} className="text-[13px]" />
            </div>
            <button
              onClick={() => onCopy(agent.fullOutput || agent.outputPreview || '')}
              className="absolute top-1 right-1 p-1 rounded bg-surface-light/80 text-text-muted hover:text-text-primary transition-colors"
              title="Copy full output"
            >
              {isCopied ? (
                <CheckCircle2 className="w-3 h-3 text-green-400" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          </div>

          {/* Sources */}
          {agent.sources && agent.sources.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] font-medium text-text-muted uppercase tracking-wide mb-1.5">
                Sources
              </div>
              <div className="flex flex-wrap gap-1.5">
                {agent.sources.map((src, i) => (
                  src.url ? (
                    <a
                      key={i}
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-light border border-border/50 text-xs text-text-primary hover:text-text-primary hover:border-border-light transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {src.title}
                    </a>
                  ) : (
                    <span
                      key={i}
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-surface-light/60 border border-border/30 text-xs text-text-secondary"
                    >
                      {src.title}
                    </span>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
